import { createFileRoute } from '@tanstack/react-router'
import { queryOptions, useQuery, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query'
import { botQueryOptions, listUsersInfiniteQuery } from '~/queries'
import { Avatar, DataListItem, DataListRoot, EmptyState, Icon, Link, Spinner } from '~/elements'
import { Boundary, Identifier, QueryParamBuilder, type FilterMenuInput, Page } from '~/components'
import { Fragment, useMemo } from 'react'
import { z } from 'zod'
import type { ClientOutputs } from '@botpress/client'
import { DateTime } from 'luxon'
import EmptyStateIcon from '~/assets/programming-04.svg?react'
import { useInfiniteScroll } from '~/hooks'
import { Button, Card, Popover, PopoverContent, PopoverTrigger, Separator } from '@bpinternal/ui-kit-next'
import { Search } from 'lucide-react'
import { getQueryKey, queryFunctions } from '~/services'
import { FILTER_SEPARATOR } from '~/components/QueryParamBuilder/parseParams'
import { useIntegrations } from '~/hooks/integrations/useIntegrations'

const usersSearchParamsSchema = z.object({
  conversationId: z
    .string()
    .optional()
    .catch(() => undefined),
  userId: z
    .string()
    .optional()
    .catch(() => undefined),
  tags: z
    .record(z.string())
    .optional()
    .catch(() => undefined),
})

const UsersPage = () => {
  const { workspaceId, botId } = Route.useParams()
  const bot = useSuspenseQuery(botQueryOptions({ botId, workspaceId })).data

  const integrationTags = useIntegrations({
    workspaceId,
    integrationIds: Object.values(bot.integrations).map((i) => i.id),
  })
    .filter((i) => Object.keys(i.user.tags).length > 0)
    .map<FilterMenuInput>((i) => ({
      name: i.name,
      icon: <Icon size="2" variant={'surface'} color={'gray'} icon={(props) => <img src={i.iconUrl} {...props} />} />,
      value: Object.keys(i.user.tags).map<FilterMenuInput>((t) => ({
        name: t,
        paramName: `tags`,
        type: 'object',
        value: `${i.name}:${t}${FILTER_SEPARATOR}`,
      })),
    }))

  const filters: FilterMenuInput[] = [
    { name: 'Conversation', paramName: 'conversationId', type: 'string' },
    { name: 'User', paramName: 'userId', type: 'string' },
    { name: 'Integration Tags', value: integrationTags },
    ...Object.keys(bot.user.tags)
      .filter((t) => !['upstream', 'downstream'].includes(t))
      .map<FilterMenuInput>((t) => ({
        name: t,
        paramName: 'tags',
        type: 'object',
        value: `${t}${FILTER_SEPARATOR}`,
      })),
  ]

  return (
    <Page title="Users">
      <div className="flex flex-col gap-4">
        <QueryParamBuilder filters={filters} />
        <Boundary loaderSize={'6'}>
          <UsersList />
        </Boundary>
      </div>
    </Page>
  )
}

const UsersList = () => {
  const { workspaceId, botId } = Route.useParams()
  const { conversationId, tags, userId } = Route.useSearch()

  const {
    data: fetchedUsers,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useSuspenseInfiniteQuery(listUsersInfiniteQuery({ botId, workspaceId, tags, conversationId }))
  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)

  const { data: fetchedUser } = useQuery(
    queryOptions({
      queryKey: getQueryKey('workspaces_/$workspaceId_/bots_/$botId_/$userId_', {
        workspaceId,
        botId,
        userId: userId ?? '',
      }),
      queryFn: async () =>
        queryFunctions['workspaces_/$workspaceId_/bots_/$botId_/$userId_']({
          workspaceId,
          botId,
          userId: userId ?? '',
        }),
      enabled: !!userId,
    })
  )

  const users = useMemo(
    () => (fetchedUser?.user ? [fetchedUser.user] : fetchedUsers.pages.flatMap((p) => p.users)),
    [fetchedUsers.pages, fetchedUser]
  )

  return (
    <>
      <Card className="p-0">
        <div className="relative grid grid-cols-[2fr,1fr,1fr,1fr] gap-x-7 text-sm">
          <div className="col-[1/-1] grid grid-cols-subgrid rounded-t-xl bg-neutral-100 p-2 font-medium">
            <p className="ml-4">User Identifier</p>
            <p>Name</p>
            <p>Tags</p>
            <p>Last Modified</p>
          </div>
          {users.length === 0 ? (
            <>
              <Separator className="col-[1/-1]" />
              <EmptyState
                iconSize={6}
                icon={EmptyStateIcon}
                className="col-[1/-1] py-16"
                title="Your bot has no users yet"
                description="Users will appear here once you start interacting with your bot."
                primaryAction={
                  <Link to="/workspaces/$workspaceId/bots/$botId/webchat" params={{ workspaceId, botId }}>
                    <Button>Start chatting!</Button>
                  </Link>
                }
              />
            </>
          ) : (
            users.map((user) => (
              <Fragment key={user.id}>
                <Separator className="col-[1/-1]" />
                <UserRow user={user} />
              </Fragment>
            ))
          )}
        </div>
      </Card>
      <div ref={bottomRef} />
      {isFetching && (
        <div className="flex w-full items-center justify-center">
          <Spinner size="6" />
        </div>
      )}
    </>
  )
}

const UserRow = ({ user }: { user: ClientOutputs['listUsers']['users'][number] }) => {
  const navigate = Route.useNavigate()
  const { id, updatedAt, name = 'Unknown User', pictureUrl, tags } = user
  const nTags = Object.keys(tags).length
  const tagsLabel = nTags === 0 ? 'No tags' : `${nTags} Tag${nTags > 1 ? 's' : ''}`

  return (
    <div className="col-[1/-1] grid grid-cols-subgrid items-center p-2">
      <div className="ml-2 flex items-center gap-2">
        <Avatar name={name} pictureUrl={pictureUrl} className="h-6 w-6 rounded-sm" variant="solid" />
        <Identifier id={id} className="w-fit" />
      </div>
      <p className="text-neutral-500">{name}</p>
      {nTags === 0 ? (
        <p className="text-xs text-neutral-950">No tags</p>
      ) : (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-12 text-brand-500 hover:bg-brand-50 hover:text-brand-500 data-[state=open]:bg-brand-50"
              onClick={(e) => e.stopPropagation()}
            >
              {tagsLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-fit bg-neutral-50" sideOffset={8} align="start">
            <DataListRoot className="text-sm">
              {Object.entries(tags).map(([tag, value]) => (
                <DataListItem highContrast key={tag} label={tag} className="items-center text-xs font-semibold">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 hover:bg-secondary-foreground/10 [&_svg]:!size-3.5"
                    onClick={() => {
                      void navigate({
                        search: (prev) => ({ ...prev, tags: { ...prev?.tags, [tag]: value } }),
                      })
                    }}
                  >
                    {value}
                    <Search className="size-3.5" />
                  </Button>
                </DataListItem>
              ))}
            </DataListRoot>
          </PopoverContent>
        </Popover>
      )}
      <p className="text-neutral-500">{DateTime.fromISO(updatedAt).toRelative()}</p>
    </div>
  )
}

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId/users')({
  validateSearch: usersSearchParamsSchema,
  component: () => (
    <Boundary height={'200px'} loaderSize="6">
      <UsersPage />
    </Boundary>
  ),
})
